/**
 * use-mcp-connector.ts — Hook cho "Kết nối AI (Claude)" (MCP connector, docs/THIET-KE-MCP-CONNECTOR.md).
 *
 * Backend `/mcp/*` (đăng nhập JWT thường; chế độ "xem hộ" bị chặn với 403 SESSION_REQUIRED):
 *  - `/mcp/settings`: bật/tắt cấp tổ chức + cho xem SĐT/email (ecdp.pii) + cho tạo nháp (ecdp.write). Sửa cần `ai_connector.update`;
 *    bật lần đầu phải kèm `acceptTerms` (ghi bằng chứng đồng ý); tắt thì thu hồi mọi kết nối.
 *  - `/mcp/connections`: kết nối của tôi, `?all=1` cả tổ chức (cần `ai_connector.view`). Thu hồi của người khác cần `ai_connector.delete`.
 *  - `/mcp/oauth/request` + `/mcp/oauth/decision`: màn đồng ý `/oauth/consent`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import type { McpScopeKey, McpScopeRequest } from '@/lib/mcp-connector'

export interface McpSettings {
  enabled: boolean
  allowPii: boolean
  allowWrite: boolean
  /** Chế độ auto: tool ghi của AI chạy ngay, không chờ nhân viên duyệt. */
  autoMode: boolean
  /** Auto có áp dụng cả nhóm rủi ro cao (xoá dữ liệu, gửi tin thật, chạy chiến dịch…). */
  autoModeHighRisk: boolean
  updatedAt: string | null
  /** URL dán vào Claude (claude.ai → Connectors, hoặc `claude mcp add`). */
  mcpUrl: string
  termsVersion: string
  /** Có quyền `ai_connector.update` — hiện các công tắc. */
  canManage: boolean
  /** Có quyền `ai_connector.view` — hiện bảng kết nối của cả tổ chức. */
  canViewAll: boolean
  /** Có quyền `ai_connector.approve` — duyệt / từ chối yêu cầu ghi của AI. */
  canApprove: boolean
  /** Số yêu cầu ghi đang chờ duyệt (0 nếu không có quyền duyệt). */
  pendingWrites: number
}

export interface McpConnection {
  id: string
  clientName: string
  redirectHost: string
  scopes: string[]
  createdAt: string
  lastUsedAt: string | null
  user: { id: string; fullName: string; email: string }
}

export type McpPendingStatus = 'pending' | 'confirmed' | 'rejected' | 'failed' | 'expired' | 'confirming'

/** Yêu cầu GHI do AI (MCP) tạo, chờ nhân viên duyệt trong eCDP. */
export interface McpPendingWrite {
  id: string
  status: McpPendingStatus
  summary: string | null
  toolName: string
  title: string
  risk: 'high' | null
  args: Record<string, unknown>
  clientName: string | null
  requestedVia: string
  requestedBy: { id: string; fullName: string | null } | null
  createdAt: string
  expiresAt: string | null
  reviewedBy: string | null
  result: { ok: boolean; text: string; at: string } | null
}

export interface McpConsentRequest {
  client: { name: string; redirectHost: string; official: boolean; local: boolean }
  org: { id: string; name: string; contactCount: number; agentCount: number }
  account: { email: string; fullName: string }
  scopes: McpScopeRequest[]
  connector: { enabled: boolean; canEnable: boolean }
}

export function useMcpSettings() {
  return useQuery<McpSettings>({
    queryKey: ['mcp', 'settings'],
    queryFn: async () => (await api.get<McpSettings>('/mcp/settings')).data,
  })
}

export function useUpdateMcpSettings() {
  const qc = useQueryClient()
  return useMutation<
    McpSettings & { revoked: number },
    unknown,
    { enabled?: boolean; allowPii?: boolean; allowWrite?: boolean; autoMode?: boolean; autoModeHighRisk?: boolean; acceptTerms?: boolean }
  >({
    mutationFn: async (body) => (await api.put('/mcp/settings', body)).data,
    onSuccess: (data) => {
      qc.setQueryData(['mcp', 'settings'], data)
      // Tắt connector thu hồi mọi kết nối → tải lại danh sách.
      if (data.revoked) qc.invalidateQueries({ queryKey: ['mcp', 'connections'] })
    },
  })
}

/** Kết nối của tôi (`all` = false) hoặc của cả tổ chức (`all` = true, cần `ai_connector.view`). */
export function useMcpConnections(all: boolean, enabled = true) {
  return useQuery<McpConnection[]>({
    queryKey: ['mcp', 'connections', all ? 'all' : 'mine'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ connections: McpConnection[] }>('/mcp/connections', { params: all ? { all: 1 } : undefined })
      return data.connections
    },
  })
}

export function useRevokeMcpConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/mcp/connections/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp', 'connections'] }),
  })
}

/**
 * Bật/tắt riêng scope ghi của MỘT kết nối đã có (không cần thu hồi + kết nối lại) — chỉ kết nối của chính mình:
 * `allowWrite` (ecdp.write: huấn luyện / nháp), `allowSend` (ecdp.send: gửi tin thật + chiến dịch), `allowDelete` (ecdp.delete: xoá). Gửi ít nhất một cờ.
 */
export function useUpdateMcpConnectionWriteScope() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; allowWrite?: boolean; allowSend?: boolean; allowDelete?: boolean }) => {
      const { id, ...body } = vars
      await api.patch(`/mcp/connections/${id}`, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp', 'connections'] }),
  })
}

/** Yêu cầu ghi của AI. `status` rỗng = tất cả; chỉ gọi khi có quyền duyệt (`canApprove`). */
export function useMcpPendingWrites(status: McpPendingStatus | '', enabled = true) {
  return useQuery<McpPendingWrite[]>({
    queryKey: ['mcp', 'pending-writes', status],
    enabled,
    refetchInterval: status === 'pending' ? 30_000 : false,
    queryFn: async () => {
      const { data } = await api.get<{ items: McpPendingWrite[] }>('/mcp/pending-writes', { params: status ? { status } : undefined })
      return data.items
    },
  })
}

function useReviewMcpWrite(action: 'approve' | 'reject') {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; text?: string }, unknown, string>({
    mutationFn: async (id) => (await api.post(`/mcp/pending-writes/${id}/${action}`)).data,
    // Duyệt thất bại (failed / quá hạn…) vẫn đổi trạng thái ở máy chủ → luôn tải lại danh sách + số chờ.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['mcp', 'pending-writes'] })
      qc.invalidateQueries({ queryKey: ['mcp', 'settings'] })
    },
  })
}

export const useApproveMcpWrite = () => useReviewMcpWrite('approve')
export const useRejectMcpWrite = () => useReviewMcpWrite('reject')

/** Phiếu xin quyền của ứng dụng (tham số `request` trên URL màn đồng ý). Phiếu hết hạn → 400 REQUEST_INVALID, không thử lại. */
export function useMcpConsentRequest(ticket: string) {
  return useQuery<McpConsentRequest>({
    queryKey: ['mcp', 'oauth-request', ticket],
    enabled: !!ticket,
    retry: false,
    queryFn: async () => (await api.get<McpConsentRequest>('/mcp/oauth/request', { params: { request: ticket } })).data,
  })
}

/** Cho phép / từ chối. Trả `redirectTo` — trình duyệt phải chuyển hẳn sang đó (về Claude / localhost kèm mã hoặc lỗi). */
export function useMcpConsentDecision() {
  return useMutation<{ redirectTo: string }, unknown, { request: string; approve: boolean; scopes?: McpScopeKey[] }>({
    mutationFn: async (body) => (await api.post('/mcp/oauth/decision', body)).data,
  })
}
